import { useQuery } from '@apollo/client';
import { GET_JOB_OFFER, GET_JOB_OFFER_APPLICATION_STATS } from '@gql/jobs';
import { Box, Typography, Card, CardContent, Grid, Chip, Skeleton, Alert, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useParams } from 'react-router-dom';

const Container = styled(Box)(({ theme }) => ({
    padding: '0px 0px 16px',
    minHeight: 'calc(100dvh - 118px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
}));

const StatCard = styled(Card)(({ theme }) => ({
    background: theme.palette.background.paper,
    borderRadius: '12px',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
}));

const StatValue = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '2rem',
    color: theme.palette.primary.main,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
}));

const SubStatusChip = styled(Chip)(({ theme }) => ({
    margin: '4px',
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.text.primary,
}));

const SourceChip = styled(Chip)(({ theme }) => ({
    margin: '4px',
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
}));

interface SubStatusCount {
    subStatus: string;
    count: number;
}

interface StatusGroup {
    status: string;
    count: number;
    subStatuses: SubStatusCount[];
}

interface SourceGroup {
    source: string;
    count: number;
}

interface JobOfferStats {
    views: number;
    applicationsByStatus: StatusGroup[];
    applicationsBySource: SourceGroup[];
}

const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
        New: 'Nouveau',
        Interview: 'Entretien',
        Trial: 'Essai',
        Offer: 'Proposition',
        ProbationPeriod: 'Période d\'essai',
        Hired: 'Embauché',
        Rejected: 'Refusé',
    };
    return labels[status] || status;
};

const getSubStatusLabel = (subStatus: string): string => {
    const labels: Record<string, string> = {
        // New
        Talent: 'Talent',
        ToContact: 'À contacter',
        ToFollowUp: 'À relancer',
        NewRejected: 'Refusé (Contact)',
        // Interview
        'To Schedule': 'À planifier',
        Scheduled: 'Planifié',
        Conducted: 'Réalisé',
        Canceled: 'Annulé',
        InterviewRejected: 'Refusé (Entretien)',
        // Trial
        TrialRejected: 'Refusé (Essai)',
        Completed: 'Réalisé',
        // Offer
        ToSend: 'À envoyer',
        Sent: 'Envoyée',
        Accepted: 'Acceptée',
        OfferRejected: 'Refusé (Proposition)',
        // ProbationPeriod
        Upcoming: 'À venir',
        InProgress: 'En cours',
        AtRisk: 'À risque',
        EmployeeResigned: 'Rupture salarié',
        EmployerTerminated: 'Rupture employeur',
    };
    return labels[subStatus] || subStatus;
};

const getSourceLabel = (source: string): string => {
    const labels: Record<string, string> = {
        upercut: 'Upercut',
        webhook: 'Webhook',
        email: 'Email',
        hellowork: 'HelloWork',
        talent: 'Talent',
        quick_apply: 'Candidature rapide',
        email_apply: 'Candidature email',
    };
    return labels[source.toLowerCase()] || source;
};

const LoadingSkeleton = () => (
    <Box sx={{ flex: 1, width: '100%' }}>
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={3}>
            <Grid md={4} xs={12} item>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: '12px' }} />
            </Grid>
        </Grid>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: '12px' }} />
    </Box>
);

const JobOfferApplicationStats = ({ proId }: { proId?: string }) => {
    const { slug } = useParams<{ slug: string }>();

    // get JobOffer Detail
    const {data: jobOfferData, loading: loadingJobOfferData, error: errorJobOfferData} = useQuery(GET_JOB_OFFER, {
        variables: { jobNumber: slug, showInactive: true },
        skip: !slug,
    });

    const { data, loading, error } = useQuery<{ getJobOfferApplicationStats: JobOfferStats }>(
        GET_JOB_OFFER_APPLICATION_STATS,
        {
            variables: { jobNumber: slug, proId: jobOfferData?.getJobOffer?.pro?._id || proId },
            skip: !slug || loadingJobOfferData || !!errorJobOfferData || (!jobOfferData?.getJobOffer?.pro?._id && !proId),
        }
    );

    if (loading) {
        return <LoadingSkeleton />;
    }

    if (error) {
        return (
            <Box sx={{ flex: 1, width: '100%' }}>
                <Alert severity="error">
                    Erreur lors du chargement des statistiques: {error.message}
                </Alert>
            </Box>
        );
    }

    const stats = data?.getJobOfferApplicationStats;

    if (!stats) {
        return (
            <Box sx={{ flex: 1, width: '100%' }}>
                <Typography color="text.secondary">
                    Aucune statistique disponible pour cette offre.
                </Typography>
            </Box>
        );
    }

    const totalApplications = stats.applicationsByStatus.reduce((sum, group) => sum + group.count, 0);

    return (
        <Stack sx={{ flex: 1, width: '100%', gap: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Statistiques des candidatures
            </Typography>

            {/* Overview Cards */}
            <Grid container spacing={3}>
                <Grid md={4} xs={12} item>
                    <StatCard>
                        <CardContent>
                            <StatLabel>Nombre de vues</StatLabel>
                            <StatValue>{stats.views.toLocaleString('fr-FR')}</StatValue>
                        </CardContent>
                    </StatCard>
                </Grid>
                <Grid md={4} xs={12} item>
                    <StatCard>
                        <CardContent>
                            <StatLabel>Total candidatures</StatLabel>
                            <StatValue>{totalApplications.toLocaleString('fr-FR')}</StatValue>
                        </CardContent>
                    </StatCard>
                </Grid>
                <Grid md={4} xs={12} item>
                    <StatCard>
                        <CardContent>
                            <StatLabel>Taux de conversion</StatLabel>
                            <StatValue>
                                {stats.views > 0
                                    ? `${((totalApplications / stats.views) * 100).toFixed(1)}%`
                                    : 'N/A'
                                }
                            </StatValue>
                        </CardContent>
                    </StatCard>
                </Grid>
            </Grid>

            {/* Applications by Status */}
            <StatCard>
                <CardContent>
                    <SectionTitle variant="h6">
                        Candidatures par statut
                    </SectionTitle>
                    <Grid container spacing={2}>
                        {stats.applicationsByStatus.map((statusGroup) => (
                            <Grid md={4} xs={12} item key={statusGroup.status}>
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {getStatusLabel(statusGroup.status)}
                                        </Typography>
                                        <Chip
                                            label={statusGroup.count}
                                            size="small"
                                            color="primary"
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {statusGroup.subStatuses.map((sub) => (
                                            <SubStatusChip
                                                key={sub.subStatus}
                                                label={`${getSubStatusLabel(sub.subStatus)}: ${sub.count}`}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </StatCard>

            {/* Applications by Source */}
            <StatCard>
                <CardContent>
                    <SectionTitle variant="h6">
                        Candidatures par source
                    </SectionTitle>
                    {stats.applicationsBySource.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {stats.applicationsBySource.map((sourceGroup) => (
                                <SourceChip
                                    key={sourceGroup.source}
                                    label={`${getSourceLabel(sourceGroup.source)}: ${sourceGroup.count}`}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">
                            Aucune source de candidature enregistrée
                        </Typography>
                    )}
                </CardContent>
            </StatCard>
        </Stack>
    );
};

export default JobOfferApplicationStats;
